import { useState, useEffect, useCallback, useRef, type MouseEventHandler, type PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';
import { ChannelList, ChannelListProps, ChannelPreviewUIComponentProps, DefaultStreamChatGenerics, useChatContext } from 'stream-chat-react';
import MessagingChannelPreview from './MessagingChannelPreview';
import MeIcon from 'assets/icons/menuIcon/me.svg?react';
import CommunitiesIcon from 'assets/icons/menuIcon/communities.svg?react';
import CommonsIcon from 'assets/icons/menuIcon/commons.svg?react';
import EcoregionIcon from 'assets/icons/menuIcon/ecoregion.svg?react';
import ActivitiesIcon from 'assets/icons/menuIcon/activities.svg?react';
import SupportIcon from 'assets/icons/menuIcon/support.svg?react';

type Category = 'messaging' | 'community' | 'commons' | 'ecoregion' | 'activity' | 'support';

type MessagingSidebarProps = {
  initialChannelListOptions: {
    filters: ChannelListProps['filters'];
    sort: ChannelListProps['sort'];
    options: ChannelListProps['options'];
  };
  onClick: MouseEventHandler;
  onCreateChannel?: () => void;
  onPreviewSelect?: MouseEventHandler;
  channelId: string;
  currentUserId: string;
};

const waitForMembership = async (channel: any, userId: string, maxRetries = 5, delay = 500) => {
  for (let i = 0; i < maxRetries; i++) {
    await channel.watch({ state: true });
    if (channel.state.members?.[userId]) return true;
    if (i < maxRetries - 1) await new Promise(r => setTimeout(r, delay * (i + 1))); // exponential backoff
  }
  throw new Error(`User ${userId} not added after ${maxRetries} retries`);
};

const MessagingSidebar = ({
  initialChannelListOptions,
  onClick,
  channelId,
  currentUserId,
  // onCreateChannel,
  // onPreviewSelect,
}: MessagingSidebarProps) => {
  const { client, setActiveChannel } = useChatContext();
  const intl = useIntl();
  const lastRequestIdRef = useRef(0);
  const [channelListOptions, setChannelListOptions] = useState(initialChannelListOptions);
  // Default: Support category, unless called as /chat/<username>
  const initialFilters = initialChannelListOptions.filters as any;
  const isMessagingContext = initialFilters?.type === 'messaging';
  const [activeCategory, setActiveCategory] = useState<Category>(
    isMessagingContext ? 'messaging' : 'support'
  );

  const handleCategoryChange = useCallback(async (category: Category) => {
    const requestId = ++lastRequestIdRef.current;
    setActiveCategory(category);

    if (category === 'support') {
      try {
        const channel = client.channel('support', 'support-chat', {
          name: 'Support Chat'
        });

        // make sure that the channel exists and add the user
        await channel.watch({ state: true });
        if (!channel.state.members?.[currentUserId]) {
          await channel.addMembers([currentUserId]);
          await waitForMembership(channel, currentUserId);
        }
        await channel.watch({
          state: true,
          presence: true
        });

        // If a newer category change happened while we were waiting, abort.
        if (lastRequestIdRef.current !== requestId) return;

        setActiveChannel(channel);

        setChannelListOptions(prev => ({
          ...prev,
          filters: {
            type: 'support',
            members: { $in: [currentUserId] }
          },
          options: {
            ...prev.options,
            state: true,
            watch: true,
            presence: false  // Groups don't need presence (incl support)
          }
        }));
      } catch (error) {
        console.error('Support chat error:', error);
      }
      return;
    }

    // Non-support: also ignore if a newer request has taken over.
    if (lastRequestIdRef.current !== requestId) return;
    setChannelListOptions(prev => ({
      ...prev,
      filters: {
        type: category,
        members: { $in: [currentUserId] },
      },
      options: {
        ...prev.options,
        state: true,
        watch: true,
        presence: category === 'messaging'  // TRUE only for 1:1 messaging chats
      }
    }));

  }, [client, currentUserId, setActiveChannel]);

  // Set proper category filters on mount
  useEffect(() => {
    const category = isMessagingContext ? 'messaging' : 'support';
    void handleCategoryChange(category);
  }, [handleCategoryChange, isMessagingContext]);


  return (
    <div
      className='str-chat messaging__sidebar str-chat__theme-light'
      id='mobile-channel-list'
      role="complementary"
      aria-label={intl.formatMessage({ id: 'chat.sidebar.aria-label.chat-channels' })}
    >
      <div className="custom-category-tabs">
        {/* support */}
        <button
          type="button"
          className={'custom-category-tab' + (activeCategory === 'support' ? ' active' : '')}
          aria-label={intl.formatMessage({ id: 'chat.sidebar.aria-label.support' })}
          onClick={() => handleCategoryChange('support')}
        >
          <div className="category-tab-icon-wrapper">
            <SupportIcon fill='#60b6aa' width="1.25rem" />
          </div>
        </button>

        {/* p2p */}
        <button
          type="button"
          className={'custom-category-tab' + (activeCategory === 'messaging' ? ' active' : '')}
          aria-label={intl.formatMessage({ id: 'chat.sidebar.aria-label.messages' })}
          onClick={() => handleCategoryChange('messaging')}
        >
          <div className="category-tab-icon-wrapper">
            <MeIcon fill='#60b6aa' width="1.25rem" />
          </div>
        </button>

        {/* activities */}
        <button
          type="button"
          className={'custom-category-tab' + (activeCategory === 'activity' ? ' active' : '')}
          aria-label={intl.formatMessage({ id: 'chat.sidebar.aria-label.activities' })}
          onClick={() => handleCategoryChange('activity')}
        >
          <div className="category-tab-icon-wrapper">
            <ActivitiesIcon fill='#60b6aa' width="1.25rem" />
          </div>
        </button>

        {/* communities */}
        <button
          type="button"
          className={'custom-category-tab' + (activeCategory === 'community' ? ' active' : '')}
          aria-label={intl.formatMessage({ id: 'chat.sidebar.aria-label.communities' })}
          onClick={() => handleCategoryChange('community')}
        >
          <div className="category-tab-icon-wrapper">
            <CommunitiesIcon fill='#60b6aa' width="1.25rem" />
          </div>
        </button>

        {/* commons */}
        <button
          type="button"
          className={'custom-category-tab' + (activeCategory === 'commons' ? ' active' : '')}
          aria-label={intl.formatMessage({ id: 'chat.sidebar.aria-label.commons' })}
          onClick={() => handleCategoryChange('commons')}
        >
          <div className="category-tab-icon-wrapper">
            <CommonsIcon fill='#60b6aa' width="1.25rem" />
          </div>
        </button>

        {/* ecoregions */}
        <button
          type="button"
          className={'custom-category-tab' + (activeCategory === 'ecoregion' ? ' active' : '')}
          aria-label={intl.formatMessage({ id: 'chat.sidebar.aria-label.ecoregions' })}
          onClick={() => handleCategoryChange('ecoregion')}
        >
          <div className="category-tab-icon-wrapper">
            <EcoregionIcon fill='#60b6aa' width="1.25rem" />
          </div>
        </button>
      </div>
      <div onClick={onClick}>
        <ChannelList
          {...channelListOptions}
          customActiveChannel={channelId}
          Preview={
            (props: PropsWithChildren<ChannelPreviewUIComponentProps<DefaultStreamChatGenerics>>) =>
              <MessagingChannelPreview {...props} />
          }
        />
      </div>
    </div>
  );
};

export default MessagingSidebar;
